import { SCROLL_DEBOUNCE_MS, SCROLL_THRESHOLD } from '@/constants/ticketConstants'

export function createScrollHandler(statusId, callback, debounceTimers) {
    return function handleScroll(element) {
        if (debounceTimers[statusId]) {
            clearTimeout(debounceTimers[statusId])
        }

        debounceTimers[statusId] = setTimeout(() => {
            const { scrollTop, scrollHeight, clientHeight } = element

            if (scrollHeight <= clientHeight) return

            const scrollPercentage = (scrollTop + clientHeight) / scrollHeight
            const isNearBottom = scrollPercentage >= SCROLL_THRESHOLD

            if (isNearBottom) {
                callback(statusId)
            }
        }, SCROLL_DEBOUNCE_MS)
    }
}

export function setupColumnScrollHandler(status, kanbanElement, scrollHandler) {
    const column = kanbanElement.querySelector(`td.e-content-cells[data-key="${status.slug}"]`)

    if (!column) {
        console.warn(`Column not found for status: ${status.slug}`)
        return null
    }

    const scrollableContent = column.querySelector('.e-card-wrapper')

    if (!scrollableContent) {
        console.warn(`Scrollable content not found for status: ${status.slug}`)
        return null
    }

    const hasOverflow = scrollableContent.scrollHeight > scrollableContent.clientHeight

    scrollableContent._scrollHandler = scrollHandler
    scrollableContent._statusId = status.id
    scrollableContent.setAttribute('data-scroll-handler', 'true')
    scrollableContent.addEventListener('scroll', scrollHandler, { passive: true })

    return scrollableContent
}

export function cleanupScrollHandlers(kanbanElement) {
    if (!kanbanElement) return

    const allScrollables = kanbanElement.querySelectorAll('[data-scroll-handler="true"]')
    allScrollables.forEach(el => {
        if (el._scrollHandler) {
            el.removeEventListener('scroll', el._scrollHandler)
            el._scrollHandler = null
            el.removeAttribute('data-scroll-handler')
        }
    })
}

export function createLoadingIndicator(statusId) {
    const loadingDiv = document.createElement('div')
    loadingDiv.className = 'column-loading-indicator'
    loadingDiv.setAttribute('data-status-id', statusId)
    loadingDiv.style.cssText = `
        display: none;
        align-items: center;
        justify-content: center;
        padding: 15px;
        margin: 10px;
        background: #f9fafb;
        border-radius: 8px;
        font-size: 13px;
        color: #667eea;
        gap: 10px;
    `
    loadingDiv.innerHTML = `
        <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="sr-only">Загрузка...</span>
        </div>
        <span>Загрузка карточек...</span>
    `
    return loadingDiv
}

export function toggleLoadingIndicator(kanbanElement, statusId, show) {
    if (!kanbanElement) return

    const indicator = kanbanElement.querySelector(`.column-loading-indicator[data-status-id="${statusId}"]`)
    if (indicator) {
        indicator.style.display = show ? 'flex' : 'none'
    }
}

/**
 * Склеивает загруженные колонки в один список карточек.
 *
 * Дедупликация по Id обязательна: колонки грузятся параллельно и каждая
 * присваивает kanbanData целиком, поэтому чужие карточки могли добавиться
 * дважды. Один и тот же Id в одной колонке — это два vnode с одинаковым key,
 * и Vue падает при следующей перерисовке списка (поиск, фильтр) с
 * «Cannot read properties of undefined (reading 'key')»: после переноса узла
 * он зануляет старый элемент, а второй поиск того же key натыкается на дырку.
 * Тикеты без Id отбрасываем по той же причине — у них key совпадает.
 */
export function mergeTicketsData(existingTickets, newTickets, statusSlug, isInitialLoad) {
    const existing = Array.isArray(existingTickets) ? existingTickets : []
    const incoming = (Array.isArray(newTickets) ? newTickets : []).filter(ticket => ticket && ticket.Id != null)

    const base = isInitialLoad
        ? existing.filter(ticket => ticket && ticket.Status !== statusSlug)
        : existing.filter(Boolean)

    const byId = new Map()
    base.forEach(ticket => {
        if (ticket.Id == null) return
        byId.set(String(ticket.Id), ticket)
    })
    // свежие данные важнее: если тикет уже был, он переехал или обновился
    incoming.forEach(ticket => byId.set(String(ticket.Id), ticket))

    return [...byId.values()]
}

export function getStatusHeaderText(status, pagination) {
    if (pagination) {
        return `${status.name} (${pagination.total})`
    }
    return status.name
}